import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import {
    isValidSiteReference,
    normaliseSiteReference,
    siteReferenceHelperText
} from './SiteReference';

interface Props {
    open: boolean;
    onClose: () => void;
}

type Version = { id: number; versionName: string; start: string };
type VersionsResponse = { availableVersions: Version[]; latestVersionId: number };
type Validation = { oldReferenceExists: boolean; newReferenceExists: boolean };

export default function ChangeSiteReferenceDialog({ open, onClose }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<Version[]>([]);
    const [versionId, setVersionId] = useState<number>(0);
    const [oldReference, setOldReference] = useState('');
    const [newReference, setNewReference] = useState('');
    const [validation, setValidation] = useState<Validation | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const reset = () => {
        setVersions([]);
        setVersionId(0);
        setOldReference('');
        setNewReference('');
        setValidation(null);
        setSubmitting(false);
        setConfirming(false);
    };

    useEffect(() => {
        if (!open) return;
        get('Admin', 'GetSiteReferenceVersions', (response: VersionsResponse) => {
            setVersions(response.availableVersions);
            setVersionId(response.latestVersionId);
        });
    }, [open]);

    const clearValidation = () => setValidation(null);
    const checkReferences = () => {
        if (!versionId || !isValidSiteReference(oldReference) || !isValidSiteReference(newReference)) return;
        const normalisedOldReference = normaliseSiteReference(oldReference);
        const normalisedNewReference = normaliseSiteReference(newReference);
        setOldReference(normalisedOldReference);
        setNewReference(normalisedNewReference);
        post('Admin', 'CheckSiteReferences', {
            oldReference: normalisedOldReference,
            newReference: normalisedNewReference,
            versionId
        }, setValidation);
    };

    const canSubmit = validation?.oldReferenceExists === true &&
        validation.newReferenceExists === false &&
        normaliseSiteReference(oldReference) !== normaliseSiteReference(newReference);

    const changeReference = async () => {
        setSubmitting(true);
        await post('Admin', 'ChangeSiteReference', {
            oldReference: normaliseSiteReference(oldReference),
            newReference: normaliseSiteReference(newReference),
            versionId
        }, () => {
            showMessage('success', 'Site reference changed successfully.');
            reset();
            onClose();
        });
        setSubmitting(false);
    };

    const close = () => {
        if (submitting || confirming) return;
        reset();
        onClose();
    };

    const selectedVersion = versions.find(version => version.id === versionId)?.versionName ?? '';

    return <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <MaintenanceDialogTitle
            icon={<DriveFileRenameOutlineOutlinedIcon />}
            title="Change site reference"
            description="Move a site's records to a new reference"
        />
        <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
                This operation changes all records for the selected release.
            </Alert>
            <TextField select fullWidth label="Version" value={versionId} disabled={submitting} sx={{ mb: 2 }}
                onChange={event => { setVersionId(Number(event.target.value)); clearValidation(); }}>
                {versions.map(version => <MenuItem key={version.id} value={version.id}>
                    {version.versionName}
                </MenuItem>)}
            </TextField>
            <TextField fullWidth label="Old site reference" placeholder="AA-01-A-01" value={oldReference} disabled={submitting} sx={{ mb: 2 }}
                onChange={event => { setOldReference(event.target.value.toUpperCase()); clearValidation(); }}
                onBlur={checkReferences}
                error={oldReference.length > 0 && !isValidSiteReference(oldReference)}
                helperText={siteReferenceHelperText} />
            <TextField fullWidth label="New site reference" placeholder="AA-01-A-01" value={newReference} disabled={submitting} sx={{ mb: 2 }}
                onChange={event => { setNewReference(event.target.value.toUpperCase()); clearValidation(); }}
                onBlur={checkReferences}
                error={newReference.length > 0 && !isValidSiteReference(newReference)}
                helperText={siteReferenceHelperText} />
            {validation && !validation.oldReferenceExists && <Alert severity="error" sx={{ mt: 1 }}>The old reference does not exist in this version.</Alert>}
            {validation && validation.newReferenceExists && <Alert severity="error" sx={{ mt: 1 }}>The new reference already exists in this version.</Alert>}
            {normaliseSiteReference(oldReference) === normaliseSiteReference(newReference) && oldReference.trim() !== '' && <Alert severity="error" sx={{ mt: 1 }}>The references must be different.</Alert>}
            {canSubmit && <Alert severity="success" sx={{ mt: 1 }}>The reference change is valid and can be submitted.</Alert>}
        </DialogContent>
        <DialogActions>
            <Button onClick={close} disabled={submitting}>Cancel</Button>
            <Button onClick={() => setConfirming(true)} disabled={!canSubmit || submitting} variant="contained">Change Reference</Button>
        </DialogActions>
        <Dialog open={confirming} onClose={() => !submitting && setConfirming(false)} maxWidth="xs" fullWidth>
            <MaintenanceDialogTitle
                icon={<ReportProblemOutlinedIcon />}
                title="Confirm reference change"
                description="Check the old and new references carefully"
                tone="warning"
            />
            <DialogContent>
                <Alert severity="warning">
                    Change <strong>{normaliseSiteReference(oldReference)}</strong> to <strong>{normaliseSiteReference(newReference)}</strong> in <strong>{selectedVersion}</strong>? This changes all records for that release.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirming(false)} disabled={submitting}>Cancel</Button>
                <Button onClick={changeReference} disabled={submitting} variant="contained" color="warning">Confirm Change</Button>
            </DialogActions>
        </Dialog>
    </Dialog>;
}
